/**
 * Utility functions for formatting various data types
 */

/**
 * Format a number as currency
 * @param amount - The amount to format
 * @param currency - The currency code to use (defaults to TZS)
 * @param locale - The locale to use for formatting (defaults to en-TZ)
 * @returns Formatted currency string
 */
export const formatCurrency = (
  amount: number | string | undefined | null,
  currency: string = 'TZS',
  locale: string = 'en-TZ'
): string => {
  // Handle undefined, null, or NaN
  if (amount === undefined || amount === null) {
    amount = 0;
  }
  
  // Convert string to number if needed
  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  // Handle NaN
  if (isNaN(numericAmount)) {
    return `0.00 ${currency}`;
  }

  // Format with Intl.NumberFormat
  return new Intl.NumberFormat(locale, { 
    style: 'currency',
    currency,
    currencyDisplay: 'symbol',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numericAmount);
};

/**
 * Format a number with commas (e.g., 1,000,000)
 * @param value - The number to format
 * @param decimalPlaces - Number of decimal places (default 2)
 * @returns Formatted number string
 */
export const formatNumber = (
  value: number | string | undefined | null,
  decimalPlaces: number = 2
): string => {
  // Handle undefined, null, or NaN
  if (value === undefined || value === null) {
    value = 0;
  }
  
  // Convert string to number if needed
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;
  
  // Handle NaN
  if (isNaN(numericValue)) {
    return '0';
  }

  return new Intl.NumberFormat('en-TZ', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces
  }).format(numericValue);
};

/**
 * Format a date to a readable string
 * @param date - The date to format (Date object or string)
 * @param format - The format to use ('short', 'medium', 'long', 'full')
 * @returns Formatted date string
 */
export const formatDate = (
  date: Date | string | undefined | null,
  format: 'short' | 'medium' | 'long' | 'full' = 'medium'
): string => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
    return '';
  }
  
  return new Intl.DateTimeFormat('en-TZ', { 
    dateStyle: format 
  }).format(dateObj);
};

/**
 * Format a percentage value
 * @param value - The value to format (0-1 or 0-100)
 * @param decimalPlaces - Number of decimal places
 * @param convertFromDecimal - Whether to convert from decimal (0-1) to percentage (0-100)
 * @returns Formatted percentage string
 */
export const formatPercentage = (
  value: number | string | undefined | null,
  decimalPlaces: number = 2,
  convertFromDecimal: boolean = true
): string => {
  // Handle undefined, null, or NaN
  if (value === undefined || value === null) {
    value = 0;
  }
  
  // Convert string to number if needed
  let numericValue = typeof value === 'string' ? parseFloat(value) : value;
  
  // Handle NaN
  if (isNaN(numericValue)) {
    return '0%';
  }

  // Convert from decimal to percentage if needed
  if (convertFromDecimal && numericValue >= 0 && numericValue <= 1) {
    numericValue = numericValue * 100;
  }

  return `${formatNumber(numericValue, decimalPlaces)}%`;
};

/**
 * Format a phone number to a standard format
 * @param phoneNumber - The phone number to format
 * @returns Formatted phone number
 */
export const formatPhoneNumber = (phoneNumber: string | undefined | null): string => {
  if (!phoneNumber) return '';
  
  // Remove any non-digit characters
  const digitsOnly = phoneNumber.replace(/\D/g, '');
  
  // Format for Tanzania (e.g., +255 123 456 789)
  if (digitsOnly.startsWith('255') && digitsOnly.length === 12) {
    return `+${digitsOnly.slice(0, 3)} ${digitsOnly.slice(3, 6)} ${digitsOnly.slice(6, 9)} ${digitsOnly.slice(9)}`;
  }
  
  // Format starting with 0 (e.g., 0123 456 789)
  if (digitsOnly.startsWith('0') && digitsOnly.length === 10) {
    return `${digitsOnly.slice(0, 4)} ${digitsOnly.slice(4, 7)} ${digitsOnly.slice(7)}`;
  }
  
  // If it doesn't match known formats, return as is with spaces
  if (digitsOnly.length > 4) {
    return digitsOnly.replace(/(\d{3})(?=\d)/g, '$1 ').trim();
  }
  
  return digitsOnly;
};
